import type { BinaryOperator, Filter } from "@cubejs-client/core"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import type { SupportedPlatform } from "~/ui-rtk/api/types/paid-media.types"
import MarathonImage from "~/ui-rtk/components/atoms/MarathonImage/MarathonImage"
import { mapMediaChannelToSourceType } from "~/ui-rtk/components/atoms/MarathonImage/helpers"
import { BrandMediaSocialPostsDashboard } from "~/ui-rtk/components/features/dashboards"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import { useCheckServiceIsConnected } from "~/ui-rtk/hooks/connected-services"
import { CONNECTORS_IDS, SOURCE_IDS } from "~/ui-rtk/constants/sources"
import type {
  TableVisualizationColumn,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"

import BlastTargetPickerModal, {
  type BlastSource,
} from "./BlastTargetPickerModal"

/**
 * AdBlaster — Ads tab (Cube-backed).
 *
 * Mounts the same dashboard wrapper used by Social Posts so the chrome
 * (sort, filter, column picker, date range, last-updated chip, pagination)
 * is identical across tabs. The active widget is the canonical paid-media
 * `brand_media_optimization_ads_table`, which is what BrandMedia/Optimization
 * also uses, so we get the metric-rich view for free.
 *
 * Scoped via `extraCubeFilters` to the platforms the company runs ads on:
 * Meta always, TikTok when either TikTok ads connector (data or Ads
 * Management) is connected. The tab itself shows when Meta OR TikTok ads are
 * connected. YouTube re-blast is a follow-up.
 *
 * Selection is keyed by the Cube `ad_id` so it survives sort/filter/pagination.
 */

const ADS_WIDGET = "brand_media_optimization_ads_table"
const ADS_WIDGET_SUMMARY = "brand_media_optimization_ads_table_summary"
const ADS_PREFIX = "company_media_metrics_ads"

const ITEMS = {
  ALL: {
    id: "ALL",
    label: "Ads",
    widget: ADS_WIDGET,
    summaryWidget: ADS_WIDGET_SUMMARY,
    // The dashboard filters its visible tabs by `requiredSources` (any-of) —
    // we want the single ALL tab to show whenever the company has connected
    // either Meta or TikTok ads. A TikTok-only company must not be locked
    // out just because Meta isn't connected.
    requiredSources: [SOURCE_IDS.META, SOURCE_IDS.TIKTOK_ADS],
  },
}

const LAST_DATE_CHIP_PROPS = {
  dateMetric:
    "company_last_updated_date.brand_media_paid_optimization_ads_max_date",
}

// Lifted out so the dashboard's columnConfig memo doesn't see a fresh object
// every render (which would force the table to re-mount and reset state).
const SELECT_COLUMN: TableVisualizationColumn = {
  key: "ad-blaster-select",
  header: "",
  type: "text",
  width: 40,
  disableSorting: true,
  disableFiltering: true,
  classes: {
    head: "text-center",
    cell: "text-center",
  },
  render: (row: TMetric, cfg) => {
    const adId = String(row.ad_id ?? "")
    if (!adId) return ""
    const selectedIds = (cfg?.selectedIds as Set<string> | undefined) ?? null
    const onToggle = cfg?.onToggle as ((id: string) => void) | undefined
    const isChecked = selectedIds?.has(adId) ?? false
    // Wrap in a span that stops click propagation so the row-level onRowClick
    // handler (which also toggles selection) doesn't fire alongside the
    // checkbox's onChange. Without this the two toggles cancel out and
    // clicking the checkbox looks like a no-op while clicking elsewhere on
    // the row works (because only the row handler fires).
    return (
      <span
        onClick={e => e.stopPropagation()}
        className="inline-flex h-full w-full items-center justify-center"
      >
        <Checkbox checked={isChecked} onChange={() => onToggle?.(adId)} />
      </span>
    )
  },
}

// The Cube widget already returns `md_image_url` as a dimension on every row,
// but the canonical column config doesn't surface it as a visible column.
// Render the preview through `<MarathonImage>` so it routes through the asset
// manager (resolve endpoint + Redux cache via `assetCacheSlice`) the same way
// the AdDetails drawer does — `md_image_url` is passed as the URL hint /
// fallback for rows whose asset hasn't been resolved yet.
const PREVIEW_COLUMN: TableVisualizationColumn = {
  key: "ad-blaster-preview",
  header: "Preview",
  type: "text",
  width: 110,
  disableSorting: true,
  disableFiltering: true,
  classes: {
    head: "text-center",
    cell: "flex items-center justify-center",
  },
  render: (row: TMetric) => {
    const adId = String(row.ad_id ?? "")
    const mediaChannel = String(row.media_channel ?? "")
    const cachedUrl = String(row.md_image_url ?? "")
    const adName = String(row.ad_name ?? "Ad preview")
    if (!adId || !mediaChannel) {
      return <div className="h-24 w-24 rounded bg-system-blue-700" />
    }
    return (
      <MarathonImage
        sourceType={mapMediaChannelToSourceType(mediaChannel)}
        externalRefId={adId}
        url={cachedUrl || undefined}
        useThumbnail
        alt={adName}
        className="h-24 w-24 rounded object-cover"
      />
    )
  },
}

// Channel filter values for the AdsTab strip. "Meta" rows include both
// IG-placed and FB-placed deliveries — the ads cube has a single
// `media_channel='meta'` dimension and splits per-channel data across
// `instagram_ads_*` / `facebook_ads_*` measures. So to filter by Instagram
// we look for rows with `instagram_ads_spend > 0`, and likewise for Facebook.
// "tiktok" filters on `media_channel='tiktok'` directly and only renders when
// the TikTok Ads Management connector is connected (the write path's gate).
type AdsChannelFilter = "all" | "instagram" | "facebook" | "tiktok"

const CHANNEL_FILTER_OPTIONS: Array<{ id: AdsChannelFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "instagram", label: "Instagram" },
  { id: "facebook", label: "Facebook" },
]

const TIKTOK_FILTER_OPTION: { id: AdsChannelFilter; label: string } = {
  id: "tiktok",
  label: "TikTok",
}

export default function AdsTab() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [channelFilter, setChannelFilter] = useState<AdsChannelFilter>("all")

  // TikTok rows show whenever the company has TikTok ads on either side:
  // the ads data connector (rows exist in the cube) or the Ads Management
  // connector (rows can be re-blasted). Either alone is enough — a company
  // whose TikTok data connector is the only thing wired up still needs to
  // see its ads here, not an empty Meta-only table.
  const hasTikTokAdsData = useCheckServiceIsConnected(CONNECTORS_IDS.TIKTOK_ADS)
  const isTikTokBlastConnected = useCheckServiceIsConnected(
    CONNECTORS_IDS.TIKTOK_ADS_MANAGEMENT,
  )
  const isTikTokConnected = hasTikTokAdsData || isTikTokBlastConnected
  const channelFilterOptions = useMemo(
    () =>
      isTikTokConnected
        ? [...CHANNEL_FILTER_OPTIONS, TIKTOK_FILTER_OPTION]
        : CHANNEL_FILTER_OPTIONS,
    [isTikTokConnected],
  )

  // Scope the widget to the platforms the company runs ads on — Meta always,
  // TikTok once either TikTok ads connector is present. The IG / FB sub-filters narrow
  // further by requiring positive spend on that placement, which is the
  // closest proxy the cube exposes for "this ad actually ran on Instagram /
  // Facebook" since `media_channel='meta'` covers both.
  const extraCubeFilters = useMemo<Filter[]>(() => {
    const base: Filter[] = [
      {
        dimension: `${ADS_PREFIX}.media_channel`,
        operator: "equals" as BinaryOperator,
        values:
          channelFilter === "tiktok"
            ? ["tiktok"]
            : channelFilter === "all" && isTikTokConnected
              ? ["meta", "tiktok"]
              : ["meta"],
      },
    ]
    if (channelFilter === "instagram") {
      base.push({
        member: `${ADS_PREFIX}.instagram_ads_spend`,
        operator: "gt",
        values: ["0"],
      })
    } else if (channelFilter === "facebook") {
      base.push({
        member: `${ADS_PREFIX}.facebook_ads_spend`,
        operator: "gt",
        values: ["0"],
      })
    }
    return base
  }, [channelFilter, isTikTokConnected])

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Mirror the row data so the bulk-blast builder can resolve a selected
  // ad_id back to ad_name + media_channel + creative preview without
  // re-firing the Cube query at submit time.
  const [rowsById, setRowsById] = useState<Map<string, TMetric>>(new Map())

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Deeplink consumption: when AdDetails (Paid Media Optimization Ads page)
  // navigates here with `?adId=<id>`, pre-check that row's checkbox so the
  // bulk-blast bar appears immediately. Strip the param afterwards so a
  // manual reload doesn't re-trigger the pre-selection (and so the user can
  // clear the selection without it springing back). Guarded with a ref so
  // the effect only acts once per navigation, even if `searchParams` is
  // mutated by an unrelated consumer of `useSearchParams`. Mirror of the
  // `?postId=` consumer in SocialPostsTab.
  const deeplinkAppliedRef = useRef(false)
  useEffect(() => {
    if (deeplinkAppliedRef.current) return
    const adId = searchParams.get("adId")
    if (!adId) return
    setSelectedIds(prev => {
      if (prev.has(adId)) return prev
      const next = new Set(prev)
      next.add(adId)
      return next
    })
    deeplinkAppliedRef.current = true
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete("adId")
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  const captureRow = useCallback((row: TMetric) => {
    const id = String(row.ad_id ?? "")
    if (!id) return
    setRowsById(prev => {
      if (prev.has(id)) return prev
      const next = new Map(prev)
      next.set(id, row)
      return next
    })
  }, [])

  const renderConfigOverride = useMemo(
    () => ({
      selectedIds,
      onToggle: (id: string) => toggleSelected(id),
      captureRow,
    }),
    [selectedIds, toggleSelected, captureRow],
  )

  // Row click toggles selection rather than opening the AdDetails drawer.
  // Captures the row (same as the checkbox cell does) so the bulk-blast
  // builder can resolve the ad later.
  const handleRowClick = useCallback(
    (row: Record<string, unknown>) => {
      const id = String(row.ad_id ?? "")
      if (!id) return
      captureRow(row as TMetric)
      toggleSelected(id)
    },
    [captureRow, toggleSelected],
  )

  const [blastModalOpen, setBlastModalOpen] = useState(false)

  const blastSources = useMemo<BlastSource[]>(() => {
    const list: BlastSource[] = []
    for (const id of selectedIds) {
      const row = rowsById.get(id)
      const name = row ? String(row.ad_name ?? "") : ""
      const mediaChannel = row ? String(row.media_channel ?? "") : ""
      const cachedUrl = row ? String(row.md_image_url ?? "") : ""
      // Hand the blast modal the asset-manager handle so its preview chip
      // routes through `<MarathonImage>` (cache-aware) instead of a bare
      // `<img>` from `md_image_url`. Same flow the table preview column
      // uses, so the chip and the row thumbnail share the same image once
      // either resolves.
      // Pass the FULL ad_name through (no truncation). The modal preview
      // chip handles visual truncation via CSS, but the backend blast call
      // forwards `source.name` as `overrides.adName` so the cloned ad on the
      // destination ad set keeps the original name verbatim. Truncating here
      // would leak a cropped name into the launched ad.
      list.push({
        kind: "ad",
        id,
        name: name || id,
        // The row's media_channel IS the blast platform ('meta' | 'tiktok').
        // Default to meta for rows whose data hasn't been captured yet.
        platform: (mediaChannel === "tiktok"
          ? "tiktok"
          : "meta") as SupportedPlatform,
        externalId: id,
        previewUrl: cachedUrl || null,
        previewType: cachedUrl ? "image" : null,
        primaryText: name || null,
        assetSourceType: mediaChannel
          ? mapMediaChannelToSourceType(mediaChannel)
          : null,
        assetExternalRefId: id,
      })
    }
    return list
  }, [selectedIds, rowsById])

  // Wrap the select column so capturing the row happens as a render
  // side-effect — keeps the data flow contained without a separate listener.
  // PREVIEW_COLUMN renders the Marathon-hosted thumbnail (`md_image_url`)
  // that the optimization widget already returns as a dimension but doesn't
  // expose by default.
  const leadingColumns = useMemo<TableVisualizationColumn[]>(
    () => [
      {
        ...SELECT_COLUMN,
        render: (row, cfg) => {
          const capture = cfg?.captureRow as ((r: TMetric) => void) | undefined
          capture?.(row)
          return SELECT_COLUMN.render?.(row, cfg)
        },
      },
      PREVIEW_COLUMN,
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-system-sm">
      {/* Media-channel filter strip — mirrors the dashboard's own channel
          buttons used by SocialPostsTab, but rendered here because the ads
          cube doesn't expose IG/FB as `media_channel` values (everything is
          'meta'). Selection drives `extraCubeFilters` above. */}
      <div className="flex flex-wrap items-center gap-system-2xs">
        {channelFilterOptions.map(option => {
          const isActive = option.id === channelFilter
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setChannelFilter(option.id)}
              className={
                isActive
                  ? "rounded-md border border-system-blue-600 bg-system-blue-600 px-system-md py-system-2xs text-sm font-semibold text-white"
                  : "rounded-md border border-system-blue-700 bg-system-blue-800 px-system-md py-system-2xs text-sm font-medium text-system-gray-300 hover:text-white"
              }
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between rounded-md border border-system-blue-600 bg-system-blue-700 px-system-md py-system-sm">
          <span className="text-sm text-white">
            {selectedIds.size} ad{selectedIds.size === 1 ? "" : "s"} selected
          </span>
          <div className="flex items-center gap-system-sm">
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-system-gray-200 hover:text-white"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => setBlastModalOpen(true)}
              className="rounded-md bg-system-green-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-green-500"
            >
              Re-blast {selectedIds.size}
            </button>
          </div>
        </div>
      )}

      <BrandMediaSocialPostsDashboard
        slug="/ad-blaster/ads"
        items={ITEMS}
        title="Ads"
        parent={{ slug: "/ad-blaster", title: "AdBlaster" }}
        lastDateChipProps={LAST_DATE_CHIP_PROPS}
        hideBreadcrumb
        hideChannelTabs
        extraCubeFilters={extraCubeFilters}
        leadingColumns={leadingColumns}
        renderConfigOverride={renderConfigOverride}
        drawerComponent="AdDetails"
        // Sort by Marathon's 180d brand value estimate — the most useful
        // ranking signal for "what should I re-blast" since it accounts for
        // long-tail value beyond direct-response revenue.
        //
        // The id is prefixed and `useOrderQueryPrefix` is true because the
        // ads widget's column keys are unprefixed (`brand_value_agg`, not
        // `company_media_metrics_ads.brand_value_agg`). Without this, Cube
        // ORDER BY clauses emitted on header clicks reference an unknown
        // column and the sort silently no-ops. Matches the BrandMedia/
        // Optimization dashboard's wiring exactly.
        defaultSortBy={`${ADS_PREFIX}.estimated_d180_brand_value`}
        useOrderQueryPrefix
        onRowClick={handleRowClick}
      />

      <BlastTargetPickerModal
        open={blastModalOpen}
        onClose={() => setBlastModalOpen(false)}
        sources={blastSources}
      />
    </div>
  )
}
